import { useCallback, useEffect, useRef, useState } from "react";

/** A finished recording: the clip bytes plus capture metadata. */
export interface ClipRecordingResult {
  blob: Blob;
  mimeType: string;
  durationSec: number;
}

/** Picks the first MediaRecorder mime type the browser supports. */
function pickMimeType(): string {
  if (typeof MediaRecorder === "undefined") {
    return "";
  }
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) ?? "";
}

/**
 * Records a video (and, when present in the stream, mic audio) clip from the
 * live camera stream using MediaRecorder. Keeps the recorded blob out of
 * React state until the recording actually finishes.
 */
export function useClipRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [result, setResult] = useState<ClipRecordingResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startMsRef = useRef(0);
  const mimeTypeRef = useRef("");
  const resultRef = useRef<ClipRecordingResult | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(
    (stream: MediaStream) => {
      setError(null);
      setResult(null);
      resultRef.current = null;
      chunksRef.current = [];

      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType, videoBitsPerSecond: 1_500_000 } : { videoBitsPerSecond: 1_500_000 },
      );
      mimeTypeRef.current = recorder.mimeType || mimeType;
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onerror = () => {
        clearTimer();
        setIsRecording(false);
        setError("Recording failed. Try a shorter clip.");
        recorderRef.current = null;
      };

      recorder.onstop = () => {
        clearTimer();
        const type = recorder.mimeType || mimeTypeRef.current || "video/webm";
        const durationSec = Math.max(
          0,
          Math.round((performance.now() - startMsRef.current) / 1000),
        );
        const clip: ClipRecordingResult = {
          blob: new Blob(chunksRef.current, { type }),
          mimeType: type,
          durationSec,
        };
        resultRef.current = clip;
        setIsRecording(false);
        setRecordingSeconds(0);
        setResult(clip);
        recorderRef.current = null;
      };

      startMsRef.current = performance.now();
      setRecordingSeconds(0);
      setIsRecording(true);
      recorder.start(1000);

      timerRef.current = setInterval(() => {
        setRecordingSeconds(Math.round((performance.now() - startMsRef.current) / 1000));
      }, 500);
    },
    [clearTimer],
  );

  /**
   * Stops the active recorder and resolves with the captured clip (or null when
   * nothing was being recorded). Used to hand the clip to the caller right when
   * a session ends, e.g. to replay it on the finish screen.
   */
  const stop = useCallback((): Promise<ClipRecordingResult | null> => {
    return new Promise((resolve) => {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === "inactive") {
        resolve(resultRef.current);
        return;
      }
      const originalOnstop = recorder.onstop;
      recorder.onstop = (event) => {
        originalOnstop?.call(recorder, event);
        resolve(resultRef.current);
      };
      try {
        recorder.stop();
      } catch {
        resolve(resultRef.current);
      }
    });
  }, []);

  const reset = useCallback(() => {
    clearTimer();
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.onstop = null;
      recorder.onerror = null;
      try {
        recorder.stop();
      } catch {
        // already stopped
      }
    }
    recorderRef.current = null;
    chunksRef.current = [];
    resultRef.current = null;
    setIsRecording(false);
    setRecordingSeconds(0);
    setResult(null);
    setError(null);
  }, [clearTimer]);

  // Clean up a recorder left running when the component unmounts.
  useEffect(() => {
    return () => {
      clearTimer();
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        recorder.onstop = null;
        try {
          recorder.stop();
        } catch {
          // already stopped
        }
      }
      recorderRef.current = null;
    };
  }, [clearTimer]);

  return {
    isRecording,
    recordingSeconds,
    result,
    error,
    start,
    stop,
    reset,
    supported: typeof MediaRecorder !== "undefined",
  };
}
